import { Component, EventEmitter, Input, OnInit, Output, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';

import { TranslateModule } from '@ngx-translate/core';
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subscription } from 'rxjs';
import { PersonType } from '../../shared/models/quotation-model/catalogs/person-type.model';
import {
  isTypePersonPhysicalConst,
  isTypePersonPhysicalEmpActConst,
} from '../../shared/models/quotation-model/constants/constants-model';
import { Quotation } from '../../shared/models/quotation-model/main/quotation.model';
import { EditClient } from '../../shared/models/quotation-model/main/user-update.model';
import { PersonTypeService } from '../../shared/services/quotation/catalogs/person-type.service';
import { EventEmiterService } from '../../shared/services/quotation/event-emiter.service';
import { QuotationService } from '../../shared/services/quotation/quotation.service';
import { MessageDialogComponent } from '../quotation-dialog/message-dialog/message-dialog.component';
import { MatInputModule } from '@angular/material/input';
import { GeneralService } from '../../shared/services/general.service';
import { RouteService } from '../../shared/services/route.service';
import { InputGeneralSelectComponent } from '../../shared/inputs/input-general-select/input-general-select.component';
import { InputGeneral } from '../../shared/inputs/input-general/input-general';
import { MatSelectChange } from '@angular/material/select';
import { DeadlinesService } from '../../shared/services/catalogs/deadlines.service';
import { PagedFilter } from '../../shared/models/components-general/general-filter.model';
import { DeadlinesPaged } from '../../shared/models/catalogs/deadline.model';

export interface Customer {
  firstName?: string;
  lastName?: string;
  secondLastName?: string;
  phoneNumber?: string;
  email?: string;
  TIN?: string;
  personTypeId?: number;
}

@Component({
  selector: 'app-lease-line',
  templateUrl: './lease-line.component.html',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    MatInputModule,
    InputGeneralSelectComponent,
    InputGeneral,
  ],
})
export class LeaseLineComponent implements OnInit, OnDestroy {
  public subscribeArray: Subscription[] = [];
  public loadService = false;
  public filter: FormGroup;

  @Output() clientReturn = new EventEmitter<{
    customer: Customer;
    selectedPersonType: PersonType;
    quotationId: any;
  }>();

  public paymentsRouter!: RouteService['paymentsRouter'];
  public selectedPersonType: PersonType = {} as PersonType;
  public personTypes: PersonType[] = [];

  public listDeadlines: DeadlinesPaged | null = null;

  public customerFormGroup!: FormGroup;
  public clientSectionColumm = 2;

  constructor(
    public dialog: MatDialog,
    private formBuilder: FormBuilder,
    private quotationService: QuotationService,
    private deadlinesService: DeadlinesService,
    private personTypeService: PersonTypeService,
    private translate: TranslateService,
    private generalService: GeneralService,
    private routeService: RouteService,
  ) {
    this.paymentsRouter = this.routeService.paymentsRouter;

    // Inicializamos el filtro para que no marque error al buscar los plazos
    this.filter = new FormGroup({
      pageSize: new FormControl('100'), // Traemos bastantes registros para llenar el select
      sortActive: new FormControl(0),
      sortDirection: new FormControl(0),
      filter: new FormControl(null),
    });

    this.customerFormGroup = this.formBuilder.group({
      firstName: ['', Validators.required],
      lastName: [''],
      secondLastName: [''],
      phoneNumber: [''],
      personType: ['', Validators.required],
      email: ['', [Validators.email]],
      TIN: ['', Validators.required],
      totalAmount: ['', Validators.required],
      rate: ['', Validators.required],
      term: ['', Validators.required], // Este es el control para el Plazo
    });
  }

  ngOnInit() {
    this.loadPersonTypes();

    // Hacemos la primera petición para cargar los plazos al iniciar
    this.getDeadlines({
      page: '1',
      pageSize: this.filter.value.pageSize,
      sortActive: this.filter.value.sortActive,
      sortDirection: this.filter.value.sortDirection,
      filter: this.filter.value.filter,
    });

    // Nos suscribimos por si cambia el filtro
    const sub = this.generalService.subscribeFormSimple(this.filter).subscribe((ress) => {
      this.getDeadlines({
        page: '1',
        pageSize: ress.pageSize,
        sortActive: ress.sortActive,
        sortDirection: ress.sortDirection,
        filter: ress.filter,
      });
    });
    this.subscribeArray.push(sub);
  }

  ngOnDestroy(): void {
    this.subscribeArray.forEach((sub) => sub.unsubscribe());
  }

  public validateOption(options: any[], control: FormControl): { invalidOption: boolean } | null {
    const selectedOption = control.value;
    if (!selectedOption || !options.some((obj) => obj.id === selectedOption)) {
      return { invalidOption: true };
    }
    return null;
  }

  public openDialogChangesWarning(): void {
    const dialogRef = this.dialog.open(MessageDialogComponent, {
      width: '350px',
      data: this.translate.instant('quotation.messages.changesDetected'),
      panelClass: 'dialog-container-custom',
    });
  }

  public async onChangePersonType(event: MatSelectChange<number>): Promise<void> {
    const option = event.source.selected;
    const typeId = event.value;
    const tinControl = this.customerFormGroup.get('TIN');
    const selectedOption = Array.isArray(option) ? option[0] : option;
    this.selectedPersonType = {
      id: typeId,
      description: selectedOption?.viewValue,
    } as PersonType;

    if (this.customerFormGroup.get('personType')?.value == 3) {
      this.customerFormGroup.controls['lastName']!.disable();
      this.customerFormGroup.controls['secondLastName']!.disable();
    } else {
      this.customerFormGroup.controls['lastName']!.enable();
      this.customerFormGroup.controls['secondLastName']!.enable();
    }

    if (tinControl) {
      if (typeId === 3) {
        tinControl.setValidators([
          Validators.required,
          Validators.minLength(12),
          Validators.maxLength(12),
          Validators.pattern(/^[a-zA-ZñÑ&]{3}\d{6}[a-zA-Z\d]{3}$/),
        ]);
      } else {
        tinControl.setValidators([
          Validators.required,
          Validators.minLength(13),
          Validators.maxLength(13),
          Validators.pattern(/^[a-zA-ZñÑ&]{4}\d{6}[a-zA-Z\d]{3}$/),
        ]);
      }
      tinControl.updateValueAndValidity();
    }
  }

  public async loadPersonTypes(): Promise<void> {
    this.personTypes = await firstValueFrom(this.personTypeService.getPersonTypes());
  }

  public loadUserData(
    firstNameObj: string,
    lastNameObj: string,
    secondLastNameObj: string,
    phoneNumberObj: string,
    personTypeObj: any,
    emailObj: string,
  ): void {
    this.customerFormGroup.patchValue({
      firstName: firstNameObj,
      lastName: lastNameObj,
      secondLastName: secondLastNameObj,
      phoneNumber: phoneNumberObj,
      personType: personTypeObj,
      email: emailObj,
    });
  }

  public isTypePersonPhysical(): boolean {
    return !!(
      this.selectedPersonType &&
      this.selectedPersonType.id &&
      (this.selectedPersonType.id === isTypePersonPhysicalConst ||
        this.selectedPersonType.id === isTypePersonPhysicalEmpActConst)
    );
  }

  public changeSelected(url: string, indexSelected: number): void {
    this.routeService.changeSelected(url, indexSelected);
  }

  private getDeadlines(body: PagedFilter): void {
    this.loadService = true;
    this.deadlinesService.getDeadlines(body).subscribe((response) => {
      if (response) {
        this.listDeadlines = response;
      }
      this.loadService = false;
    });
  }

  public sendToIntegration(): void {
    this.loadService = true;
    this.quotationService.insertLeaseLine(this.customerFormGroup.value).subscribe((response) => {
      this.loadService = false;

      if (response) {
        this.customerFormGroup.reset();
        this.generalService.viewSnacAlert(
          'Operación enviada a Integración para su revisión',
          'normal',
          'Completado',
        );
      }
    });
  }
}
